import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export type SortState = { sort: string | null; direction: "asc" | "desc" };

/**
 * A column header that sorts.
 *
 * Sorting is a round trip, not a client-side reorder: both tables are windows
 * onto more rows than they show — 470 people, 430 legacies — so reordering the
 * fifty already fetched would answer a different question than the header asks.
 *
 * The arrow only appears on the sorted column; the others show a faint
 * both-ways glyph on hover, so the header row does not read as decorated.
 */
export function SortableHeader({
  column,
  state,
  onSort,
  className,
  align = "left",
  children,
}: {
  column: string;
  state: SortState;
  onSort: (next: SortState) => void;
  className?: string;
  align?: "left" | "right";
  children: React.ReactNode;
}) {
  const active = state.sort === column;
  const next: SortState = active
    ? { sort: column, direction: state.direction === "asc" ? "desc" : "asc" }
    : { sort: column, direction: "asc" };
  const Icon = active ? (state.direction === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(next)}
        aria-sort={active ? (state.direction === "asc" ? "ascending" : "descending") : "none"}
        className={cn(
          "group/sort inline-flex items-center gap-1 hover:text-foreground",
          align === "right" && "w-full justify-end",
          active ? "text-foreground font-medium" : "text-muted-foreground"
        )}
      >
        {children}
        <Icon
          className={cn(
            "size-3 shrink-0",
            active ? "opacity-100" : "opacity-0 group-hover/sort:opacity-40"
          )}
        />
      </button>
    </TableHead>
  );
}
